const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

// Helper para obtener headers de autenticación
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Map backend Grupo to UI row matching the table structure shown in the reference image
function mapApiToRow(api) {
  // Formatear modalidad: PRESENCIAL → Presencial, EN_LINEA → En línea, etc.
  let modalidadDisplay = '—';
  if (api.modalidad === 'PRESENCIAL') modalidadDisplay = 'Presencial';
  else if (api.modalidad === 'EN_LINEA') modalidadDisplay = 'En línea';
  else if (api.modalidad === 'VIRTUAL_HIBRIDA') modalidadDisplay = 'Virtual';
  else if (api.modalidad) modalidadDisplay = api.modalidad;
  
  // Formatear tipo_grupo: ABIERTO → Abierto, CERRADO → Cerrado
  let tipoDisplay = '—';
  if (api.tipo_grupo === 'ABIERTO') tipoDisplay = 'ABIERTO';
  else if (api.tipo_grupo === 'CERRADO') tipoDisplay = 'CERRADO';
  else if (api.tipo_grupo) tipoDisplay = api.tipo_grupo;
  
  // Formatear horario - extraer bloque de horario o información legible
  let horarioDisplay = '—';
  if (api.horario) {
    try {
      let horarioData = api.horario;
      // Si es string, intentar parsear
      if (typeof horarioData === 'string') {
        try { horarioData = JSON.parse(horarioData); } catch { horarioDisplay = horarioData; }
      }
      // Si es objeto, extraer bloque o detalle
      if (typeof horarioData === 'object') {
        if (horarioData.bloque) {
          horarioDisplay = horarioData.bloque;
        } else if (horarioData.detalle_texto) {
          horarioDisplay = horarioData.detalle_texto;
        } else if (horarioData.hora_inicio && horarioData.hora_fin) {
          horarioDisplay = `${horarioData.hora_inicio}-${horarioData.hora_fin}`;
        } else {
          horarioDisplay = 'Definido';
        }
      }
    } catch (e) {
      horarioDisplay = 'Definido';
    }
  }
  
  // Formatear cupo: inscritos_actuales/cupo_maximo
  const inscritosActuales = api.inscritos_actuales !== undefined ? api.inscritos_actuales : 0;
  const cupoMaximo = api.cupo_maximo || 0;
  const cupoDisplay = `${inscritosActuales}/${cupoMaximo}`;
  
  // Campus (nombre legible)
  const campusDisplay = api.campus_nombre || api.campus || api.nombre_campus || '—';

  // Sección académica (nombre/código legible)
  const seccionDisplay = api.seccion || api.seccion_nombre || api.nombre_seccion || api.codigo_seccion || '—';
  
  // Determinar estado badge para el componente StatusBadge
  let estadoBadge = 'tentativo';
  if (api.estado_grupo === 'ABIERTO') estadoBadge = 'activo';
  else if (api.estado_grupo === 'CERRADO') estadoBadge = 'cerrado';
  else if (api.estado_grupo === 'CANCELADO') estadoBadge = 'cancelado';
  else if (api.estado_grupo === 'TENTATIVO') estadoBadge = 'tentativo';
  
  // Turno (de columna turno, no display)
  const turnoReal = api.turno || '—';

  // Programa Académico
  const programaDisplay = api.programa_academico_nombre || '—';

  return {
    id: api.id_grupo || api.codigo_grupo || '—',  // Usar id_grupo como PK para update/delete
    id_grupo: api.id_grupo,
    id_materia: api.id_materia || api.id_materia_id, // ID de la materia para filtros
    codigo_grupo: api.codigo_grupo || '—',
    materia: api.materia || api.materia_nombre || '—',
    campus: campusDisplay,
    asesor: api.asesor_nombre || '—',
    seccion: seccionDisplay,
    ciclo: api.ciclo_nombre || '—',
    tipo: tipoDisplay,
    cupo: cupoDisplay,
    modalidad: modalidadDisplay,
    turno: turnoReal,
    programa: programaDisplay,
    estado: api.estado_grupo || 'TENTATIVO',
    estadoBadge: estadoBadge,
    // Campos completos del API para usar en modals de edición/vista
    _raw: api
  };
}

export async function getGroups() {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...getAuthHeaders()
  };
  console.log('[groups.js] Fetching grupos desde:', `${API_BASE}/escolar/grupos/`);
  console.log('[groups.js] Headers:', headers);
  const res = await fetch(`${API_BASE}/escolar/grupos/`, { headers });
  console.log('[groups.js] Response status:', res.status, res.statusText);
  console.log('[groups.js] Response headers:', Object.fromEntries(res.headers.entries()));
  if (!res.ok) {
    const errorText = await res.text();
    console.error('[groups.js] Error response:', errorText);
    throw new Error(`GET /grupos/ ${res.status}: ${errorText}`);
  }
  const text = await res.text();
  console.log('[groups.js] Response text (primeros 500 chars):', text.substring(0, 500));
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    console.error('[groups.js] Error parsing JSON:', e);
    return [];
  }
  console.log('[groups.js] Datos parseados:', data?.length, 'registros');
  return Array.isArray(data) ? data.map(mapApiToRow) : [];
}

export function mapFormToPayloadGeneral(form, opts = {}) {
  // Map UI values to backend expected values and types
  // Choices: tipo_grupo, modalidad, estado_grupo, etc.
  // Foreign keys: enviar PK numérico solo si existe en catálogo
  function toInt(val) {
    if (val === undefined || val === null || val === '') return null;
    const n = parseInt(val, 10);
    return Number.isFinite(n) ? n : null;
  }

  // Choices EXACTOS según Django
  // tipo_grupo: 'ABIERTO' | 'CERRADO'
  const tipoGrupo = form.tipoGrupo === 'ABIERTO' || form.tipoGrupo === 'CERRADO' ? form.tipoGrupo : null;
  // modalidad: 'PRESENCIAL' | 'EN_LINEA' | 'VIRTUAL_HIBRIDA'
  let modalidad = form.modalidad;
  if (modalidad === 'Presencial') modalidad = 'PRESENCIAL';
  else if (modalidad === 'En línea') modalidad = 'EN_LINEA';
  else if (modalidad === 'Virtual/Híbrida') modalidad = 'VIRTUAL_HIBRIDA';
  if (!['PRESENCIAL', 'EN_LINEA', 'VIRTUAL_HIBRIDA'].includes(modalidad)) modalidad = null;
  // estado_grupo: 'TENTATIVO' | 'ABIERTO' | 'CERRADO' | 'CANCELADO'
  let estadoGrupo = form.estado;
  if (!['TENTATIVO', 'ABIERTO', 'CERRADO', 'CANCELADO'].includes(estadoGrupo)) {
    // Default seguro cuando viene vacío o con un valor no válido
    estadoGrupo = 'TENTATIVO';
  }


  // Solo enviar PKs numéricos válidos, si no, null
  const idMateria = toInt(form.materiaId) || null;
  const idAsesor = toInt(form.profesorId) || null;
  const idCampus = toInt(form.campusId) || null;
  const idSeccion = toInt(form.seccionId) || null;
  const idAcademia = toInt(form.academia) || null;
  
  console.log('[groups.js] Mapeando form a payload:', {
    materiaId: form.materiaId,
    idMateria,
    profesorId: form.profesorId,
    idAsesor,
    campusId: form.campusId,
    idCampus,
    seccionId: form.seccionId,
    idSeccion,
    academia: form.academia,
    idAcademia
  });
  
  // id_ciclo_escolar: solo enviar si es un PK válido (no año ni string)
  let idCicloEscolar = toInt(form.cicloEscolar);
  if (!idCicloEscolar) idCicloEscolar = null;
  
  // Mapear tipoPeriodo al campo tipo_periodo del backend
  // tipoPeriodo contiene valores como 'SEMESTRAL', 'TETRAMESTRAL'
  let tipoPeriodo = form.tipoPeriodo || null;
  if (tipoPeriodo && typeof tipoPeriodo === 'string') {
    // Normalizar a mayúsculas en caso de que venga en otro formato
    tipoPeriodo = tipoPeriodo.toUpperCase();
    // Validar que sea uno de los valores permitidos
    if (!['SEMESTRAL', 'TETRAMESTRAL'].includes(tipoPeriodo)) {
      tipoPeriodo = null;
    }
  }
  
  // periodoId es un campo opcional para periodo_numero
  const periodoNumero = toInt(form.periodoId) || null;

  // Programa Académico (FK opcional)
  const idProgramaAcademico = toInt(form.programaAcademicoId) || null;

  // Turno / Bloque horario – se persiste como campo propio en esc_grupos
  const turno = form.bloqueHorario || null;

  const payload = {
    codigo_grupo: form.codigoGrupo.trim(),
    id_materia: idMateria,
    id_asesor: idAsesor,
    id_seccion: idSeccion,
    id_academia: idAcademia,
    tipo_grupo: tipoGrupo,
    id_campus: idCampus,
    modalidad: modalidad,
    cupo_maximo: Number(form.cupoMaximo || 0),
    cupo_minimo: Number(form.cupoMinimo || 0),
    cupo_actual: Number(form.cupoActual || 0),
    periodo_numero: periodoNumero,
    tipo_periodo: tipoPeriodo,
    horario: form.horarioJson || null,
    aula: form.aula || '',
    estado_grupo: estadoGrupo,
    id_institucion: toInt(form.institucion) || 1, // Usar el valor del form o 1 por defecto
    id_ciclo_escolar: idCicloEscolar,
    id_programa_academico: idProgramaAcademico,
    turno: turno,
  };
  
  console.log('[groups.js] ===== PAYLOAD FINAL =====');
  console.log('[groups.js] Payload:', JSON.stringify(payload, null, 2));
  
  // Validación crítica: id_ciclo_escolar es requerido
  if (!payload.id_ciclo_escolar) {
    console.error('[groups.js] ❌ ERROR: id_ciclo_escolar es NULL - El backend requiere este campo');
  }
  
  return payload;
}

// Helper para extraer mensajes de error amigables del backend
function parseBackendError(errorText) {
  try {
    const errorData = JSON.parse(errorText);
    // Mapeo de campos técnicos a nombres amigables
    const fieldLabels = {
      'codigo_grupo': 'Código de Grupo',
      'id_materia': 'Materia',
      'id_asesor': 'Asesor',
      'id_campus': 'Campus',
      'id_seccion': 'Sección Académica',
      'id_academia': 'Academia',
      'id_ciclo_escolar': 'Ciclo Escolar',
      'id_institucion': 'Institución',
      'tipo_grupo': 'Tipo de Grupo',
      'estado_grupo': 'Estado',
      'modalidad': 'Modalidad',
      'cupo_maximo': 'Cupo Máximo',
      'cupo_minimo': 'Cupo Mínimo',
      'periodo_numero': 'Período',
      'aula': 'Aula',
      'non_field_errors': 'Error general',
    };
    
    const messages = [];
    for (const [field, errors] of Object.entries(errorData)) {
      const label = fieldLabels[field] || field;
      const errorMsg = Array.isArray(errors) ? errors[0] : errors;
      // Traducir mensajes comunes
      let friendlyMsg = errorMsg;
      if (errorMsg.includes('may not be null') || errorMsg.includes('is required')) {
        friendlyMsg = 'es obligatorio';
      } else if (errorMsg.includes('Invalid pk') || errorMsg.includes('does not exist')) {
        friendlyMsg = 'selecciona una opción válida';
      } else if (errorMsg.includes('already exists')) {
        friendlyMsg = 'ya existe';
      }
      messages.push(`${label}: ${friendlyMsg}`);
    }
    return messages.length > 0 ? messages.join('\n') : 'Error al guardar el grupo';
  } catch {
    return 'Error al guardar el grupo. Verifica los datos ingresados.';
  }
}

export async function createGroup(form) {
  const payload = mapFormToPayloadGeneral(form);
  const res = await fetch(`${API_BASE}/escolar/grupos/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text();
    const friendlyError = parseBackendError(text);
    throw new Error(friendlyError);
  }
  // Evitar hacer un GET completo de grupos aquí para no duplicar carga.
  let created = null;
  try {
    created = await res.json();
  } catch (_) {
    // Si la respuesta no tiene cuerpo JSON, lo ignoramos.
  }
  try { window.dispatchEvent(new CustomEvent('groups:updated')); } catch (_) {}
  return created;
}

export async function getPeriodosEscolares() {
  // Este endpoint no existe en el backend, retornar array vacío
  // Los tipos de periodo ya se obtienen con getPeriodTypes() desde periodTypes.js
  return [];
}

// Update an existing group (by codigo_grupo as id)
export async function updateGroup(id, form) {
  const payload = mapFormToPayloadGeneral(form);
  console.log('[updateGroup] Payload enviado al backend:', payload);
  const res = await fetch(`${API_BASE}/escolar/grupos/${id}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text();
    const friendlyError = parseBackendError(text);
    throw new Error(friendlyError);
  }
  let updatedGroup = null;
  try {
    updatedGroup = await res.json();
  } catch (_) {
    // El ViewSet puede devolver una respuesta mínima sin JSON.
  }
  try { window.dispatchEvent(new CustomEvent('groups:updated')); } catch (_) {}
  return updatedGroup;
}

// Delete a group (by codigo_grupo as id)
export async function deleteGroup(id) {
  const res = await fetch(`${API_BASE}/escolar/grupos/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    throw new Error('No se pudo eliminar el grupo. Intenta nuevamente.');
  }
  // Solo notificar que hubo cambios; los listados decidirán si recargan.
  try { window.dispatchEvent(new CustomEvent('groups:updated')); } catch (_) {}
  return true;
}

/**
 * Obtener grupos filtrados por materias, campus, ciclo, etc.
 * @param {Object} params - Parámetros de filtrado
 * @param {Array|string} params.materias - IDs de materias (array o string separado por comas)
 * @param {number} params.id_campus - ID del campus
 * @param {number} params.id_ciclo_escolar - ID del ciclo escolar
 * @param {string} params.estado_grupo - Estado del grupo (ABIERTO, CERRADO, etc.)
 * @param {boolean} params.con_cupo - Solo grupos con cupo disponible
 * @returns {Promise<Array>} Lista de grupos que coinciden con los filtros
 */
export async function getGruposFiltrados(params = {}) {
  try {
    const queryParams = new URLSearchParams();
    
    // Materias: puede ser array o string
    if (params.materias) {
      const materiasStr = Array.isArray(params.materias) 
        ? params.materias.join(',') 
        : params.materias;
      queryParams.append('id_materia', materiasStr);
    }
    
    if (params.id_campus) {
      queryParams.append('id_campus', params.id_campus);
    }
    
    // Filtrar por sección académica (licenciatura, preparatoria, etc.)
    if (params.id_seccion) {
      queryParams.append('id_seccion', params.id_seccion);
    }
    
    // Filtrar por programa académico (filtra grupos de materias del plan de estudios del programa)
    if (params.id_programa) {
      queryParams.append('id_programa', params.id_programa);
    }
    
    if (params.id_ciclo_escolar) {
      queryParams.append('id_ciclo_escolar', params.id_ciclo_escolar);
    }

    if (params.estado_ciclo) {
      queryParams.append('estado_ciclo', params.estado_ciclo);
    }

    if (params.tipo_periodo) {
      queryParams.append('tipo_periodo', params.tipo_periodo);
    }

    if (params.modalidad) {
      queryParams.append('modalidad', params.modalidad);
    }
    
    if (params.estado_grupo) {
      queryParams.append('estado_grupo', params.estado_grupo);
    }
    
    if (params.con_cupo) {
      queryParams.append('con_cupo', 'true');
    }
    
    if (params.activo !== undefined) {
      queryParams.append('activo', params.activo);
    }
    
    // Búsqueda textual (código, materia, asesor, etc.)
    if (params.search) {
      queryParams.append('search', params.search);
    }
    
    // Solicitar todos los resultados sin paginación para pantallas de asignación
    if (params.no_pagination) {
      queryParams.append('no_pagination', 'true');
    }
    
    const url = `${API_BASE}/escolar/grupos/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    console.log('[groups.js] Fetching grupos filtrados desde:', url);
    
    const res = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...getAuthHeaders()
      }
    });
    
    if (!res.ok) {
      console.error('[groups.js] Error obteniendo grupos filtrados:', res.status);
      return [];
    }
    
    const data = await res.json();
    const grupos = Array.isArray(data) ? data : (data.results || []);
    
    return grupos.map(mapApiToRow);
  } catch (error) {
    console.error('[groups.js] Error en getGruposFiltrados:', error);
    return [];
  }
}

/**
 * Obtener las materias ASIGNABLES de un estudiante + sus grupos disponibles.
 *
 * Consume el servicio orquestador del backend
 * (GET /escolar/asignacion-grupos/materias-asignables/<id>/), desacoplado de
 * Preasignación. La fuente de verdad académica es el Kardex oficial: solo se
 * excluyen las materias APROBADAS. Los grupos disponibles corresponden a
 * cualquier ciclo cuyo estado sea PLANEADO o ACTIVO.
 *
 * @param {number|string} estudianteId
 * @param {Object} params - { id_campus, id_seccion, tipo_periodo, estado_grupo, estado_ciclo }
 * @returns {Promise<{meta: Object, materias: Array, grupos_disponibles: Array}>}
 *          Los grupos (tanto en cada materia como en la lista plana) vienen
 *          mapeados con el mismo formato de fila que getGruposFiltrados.
 */
export async function getMateriasAsignables(estudianteId, params = {}) {
  const empty = { meta: {}, materias: [], grupos_disponibles: [] };
  if (!estudianteId) {
    console.warn('[groups.js] getMateriasAsignables: estudianteId es null/undefined');
    return empty;
  }
  try {
    const queryParams = new URLSearchParams();
    if (params.id_campus) queryParams.append('id_campus', params.id_campus);
    if (params.id_seccion) queryParams.append('id_seccion', params.id_seccion);
    if (params.tipo_periodo) queryParams.append('tipo_periodo', params.tipo_periodo);
    if (params.estado_grupo) queryParams.append('estado_grupo', params.estado_grupo);
    if (params.estado_ciclo) queryParams.append('estado_ciclo', params.estado_ciclo);

    const qs = queryParams.toString();
    const url = `${API_BASE}/escolar/asignacion-grupos/materias-asignables/${estudianteId}/${qs ? '?' + qs : ''}`;
    console.log('[groups.js] Fetching materias asignables desde:', url);

    const res = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...getAuthHeaders()
      }
    });

    if (!res.ok) {
      console.error('[groups.js] Error obteniendo materias asignables:', res.status);
      return empty;
    }

    const data = await res.json();
    const materias = (data.materias || []).map(m => ({
      ...m,
      grupos_disponibles: (m.grupos_disponibles || []).map(mapApiToRow),
    }));
    const gruposDisponibles = (data.grupos_disponibles || []).map(mapApiToRow);

    return {
      meta: data.meta || {},
      materias,
      grupos_disponibles: gruposDisponibles,
    };
  } catch (error) {
    console.error('[groups.js] Error en getMateriasAsignables:', error);
    return empty;
  }
}

/**
 * Obtener grupos paginados y filtrados, similar a getStudents en Asignacion de Becas.
 * Devuelve tanto los registros mapeados como el total para manejar paginación en frontend.
 *
 * @param {Object} params
 * @param {number} params.page - Página a solicitar (1-based)
 * @param {number} params.page_size - Tamaño de página
 * @param {number} params.id_campus - ID del campus
 * @param {number} params.id_ciclo_escolar - ID del ciclo escolar
 * @param {string} params.estado_grupo - Estados separados por coma
 * @param {string} params.search - Búsqueda textual (código, materia, asesor, sección, etc.)
 * @returns {Promise<{results: Array, count: number}>}
 */
export async function getGroupsPaginated(params = {}) {
  try {
    const queryParams = new URLSearchParams();

    if (params.page) {
      queryParams.append('page', params.page);
    }
    if (params.page_size) {
      queryParams.append('page_size', params.page_size);
    }
    if (params.id_campus) {
      queryParams.append('id_campus', params.id_campus);
    }
    if (params.id_ciclo_escolar) {
      queryParams.append('id_ciclo_escolar', params.id_ciclo_escolar);
    }
    if (params.estado_ciclo) {
      queryParams.append('estado_ciclo', params.estado_ciclo);
    }
    if (params.tipo_periodo) {
      queryParams.append('tipo_periodo', params.tipo_periodo);
    }
    if (params.modalidad) {
      queryParams.append('modalidad', params.modalidad);
    }
    if (params.estado_grupo) {
      queryParams.append('estado_grupo', params.estado_grupo);
    }
    if (params.con_cupo) {
      queryParams.append('con_cupo', 'true');
    }
    if (params.activo !== undefined) {
      queryParams.append('activo', params.activo);
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }

    const url = `${API_BASE}/escolar/grupos/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    console.log('[groups.js] Fetching grupos paginados desde:', url);

    const res = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!res.ok) {
      console.error('[groups.js] Error obteniendo grupos paginados:', res.status);
      return { results: [], count: 0 };
    }

    const data = await res.json();
    let rawItems;
    let count;

    if (Array.isArray(data)) {
      rawItems = data;
      count = data.length;
    } else {
      rawItems = Array.isArray(data.results) ? data.results : [];
      count = typeof data.count === 'number' ? data.count : rawItems.length;
    }

    const mapped = rawItems.map(mapApiToRow);
    return { results: mapped, count };
  } catch (error) {
    console.error('[groups.js] Error en getGroupsPaginated:', error);
    return { results: [], count: 0 };
  }
}

